import type { CompanyShellValidationError } from "./companyShellValidation";

/**
 * `company_shell_provision` workflow result for `mode: "commit"`. Not
 * formalized in OpenAPI — see Pacific.Agentic.Api
 * `CompanyShellProvisionResult` / `RowResult` and Pacific.Agentic.Api#834.
 */
export type CompanyShellCommitWorkflowStatus = "completed" | "failed";

export type CompanyShellRowResultStatus =
  | "committed"
  | "failed_requires_intervention"
  | "skipped";

export interface CompanyShellRowResult {
  row_index: number;
  status: CompanyShellRowResultStatus;
  company_code?: string | null;
  company_id?: string | null;
  errors?: CompanyShellValidationError[];
}

export interface CompanyShellCommitResult {
  status: CompanyShellCommitWorkflowStatus;
  mode: "commit";
  row_count: number;
  committed: number;
  failed: number;
  errors: CompanyShellValidationError[];
  rows: CompanyShellRowResult[];
}

export type CompanyShellCommitOutcomeKind =
  | "full_success"
  | "partial"
  | "all_failed";

const ROW_RESULT_STATUSES: ReadonlySet<string> = new Set([
  "committed",
  "failed_requires_intervention",
  "skipped",
]);

const WORKFLOW_STATUSES: ReadonlySet<string> = new Set(["completed", "failed"]);

function isValidationError(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const e = value as Record<string, unknown>;
  return (
    typeof e.row_index === "number" &&
    typeof e.message === "string" &&
    typeof e.severity === "string"
  );
}

function isRowResult(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const row = value as Record<string, unknown>;
  if (typeof row.row_index !== "number") return false;
  if (typeof row.status !== "string" || !ROW_RESULT_STATUSES.has(row.status)) {
    return false;
  }
  if (
    row.company_code !== undefined &&
    row.company_code !== null &&
    typeof row.company_code !== "string"
  ) {
    return false;
  }
  if (
    row.company_id !== undefined &&
    row.company_id !== null &&
    typeof row.company_id !== "string"
  ) {
    return false;
  }
  if (row.errors !== undefined) {
    if (!Array.isArray(row.errors)) return false;
    if (!row.errors.every(isValidationError)) return false;
  }
  return true;
}

/**
 * Runtime type-guard for the `company_shell_provision` workflow's `mode:
 * "commit"` result on `AgentInvocationStatus.result`.
 */
export function isCompanyShellCommitResult(
  value: unknown
): value is CompanyShellCommitResult {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  if (candidate.mode !== "commit") return false;
  if (
    typeof candidate.status !== "string" ||
    !WORKFLOW_STATUSES.has(candidate.status)
  ) {
    return false;
  }
  if (typeof candidate.row_count !== "number") return false;
  if (typeof candidate.committed !== "number") return false;
  if (typeof candidate.failed !== "number") return false;
  if (!Array.isArray(candidate.errors)) return false;
  if (!candidate.errors.every(isValidationError)) return false;
  if (!Array.isArray(candidate.rows)) return false;
  if (!candidate.rows.every(isRowResult)) return false;
  return true;
}

/** Rows whose commit saga completed successfully. */
export function getCommittedRowResults(
  result: CompanyShellCommitResult
): CompanyShellRowResult[] {
  return result.rows.filter((row) => row.status === "committed");
}

/** Rows that did not commit successfully (failed, skipped, etc.). */
export function getNonCommittedRowResults(
  result: CompanyShellCommitResult
): CompanyShellRowResult[] {
  return result.rows.filter((row) => row.status !== "committed");
}

/** Whether any row failed or the roll-up reports failures (AC-NFR-12). */
export function hasCommitFailure(result: CompanyShellCommitResult): boolean {
  if (result.failed > 0) return true;
  if (result.row_count === 0) return false;
  if (result.committed < result.row_count) return true;
  return result.rows.some((row) => row.status !== "committed");
}

/**
 * Coarse outcome for Results banners and lookup fan-out. Must use the
 * row-aware `hasCommitFailure` check (not just the `committed`/`failed`
 * roll-up counters) so a `skipped` row — which is neither `committed` nor
 * counted in `failed` — can't slip through as a `full_success` and show a
 * green banner (AC-NFR-12).
 */
export function commitOutcomeKind(
  result: CompanyShellCommitResult
): CompanyShellCommitOutcomeKind {
  if (result.row_count === 0) {
    return result.failed > 0 ? "all_failed" : "full_success";
  }
  if (!hasCommitFailure(result)) {
    return "full_success";
  }
  if (getCommittedRowResults(result).length === 0) {
    return "all_failed";
  }
  return "partial";
}
